/**
 * Tabela da Matriz RACI (GUPM-192): linhas por seção, uma coluna por parte.
 * Rola na horizontal dentro do próprio contêiner (mobile-first) com a coluna
 * "Atividade" fixa; a página não ganha rolagem lateral.
 */
import {
  PARTES_RACI,
  RACI_LEGENDA,
  type ParteRaciId,
  type RaciLetra,
  type SecaoRaci,
} from '@/domain/outros';

const TOTAL_COLUNAS = PARTES_RACI.length + 1;

/**
 * Letra RACI com a cor da legenda. Na tabela, o leitor de tela ouve o significado
 * ("R, Responsável pela execução"); na legenda (`rotular=false`) o rótulo já está
 * ao lado, visível.
 */
export function CelulaRaci({ letra, rotular = true }: { letra: RaciLetra | null; rotular?: boolean }) {
  if (letra === null) {
    return (
      <>
        <span aria-hidden className="text-jf-dim/60">·</span>
        <span className="sr-only">sem atribuição</span>
      </>
    );
  }
  const cor = RACI_LEGENDA[letra];
  return (
    <span
      title={cor.rotulo}
      className="inline-flex h-6 w-6 items-center justify-center rounded border text-[11px] font-bold"
      style={{ background: cor.fundo, color: cor.texto, borderColor: cor.borda }}
    >
      {letra}
      {rotular && <span className="sr-only">, {cor.rotulo}</span>}
    </span>
  );
}

interface Props {
  secoes: readonly SecaoRaci[];
  destaque: ParteRaciId | null;
}

export default function MatrizRaciTabela({ secoes, destaque }: Props) {
  const colunaClasse = (parte: ParteRaciId) => (parte === destaque ? 'bg-jf-yellow/10' : '');

  return (
    // `relative` é o que prende os `sr-only` (absolutos) das células vazias dentro da
    // rolagem: sem ele, o containing block deles fica fora do contêiner e a PÁGINA
    // inteira ganha rolagem lateral no celular.
    <div className="relative max-w-full overflow-x-auto rounded-lg border border-jf-border">
      <table aria-label="Matriz RACI" className="w-full min-w-[760px] border-collapse text-xs">
        <thead>
          <tr className="bg-jf-surface2 text-jf-muted">
            <th
              scope="col"
              className="sticky left-0 z-10 bg-jf-surface2 px-2 py-2 text-left font-bold text-white"
            >
              Atividade
            </th>
            {PARTES_RACI.map((p) => (
              <th
                key={p.id}
                scope="col"
                title={p.nome}
                data-destaque={p.id === destaque ? 'true' : undefined}
                className={`w-12 px-1 py-2 text-center font-bold ${
                  p.id === destaque ? 'bg-jf-yellow/15 text-jf-yellow' : ''
                }`}
              >
                {p.id}
              </th>
            ))}
          </tr>
        </thead>
        {secoes.map((s) => (
          <tbody key={s.titulo}>
            <tr className="border-t border-jf-border bg-jf-surface2/60">
              <td
                colSpan={TOTAL_COLUNAS}
                className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-jf-dim"
              >
                {/* sticky: no celular o título da seção não sai da tela ao rolar na horizontal */}
                <span className="sticky left-2 inline-block">{s.titulo}</span>
              </td>
            </tr>
            {s.atividades.map((a) => (
              <tr key={a.id} className="border-t border-jf-border/60 align-middle">
                <th
                  scope="row"
                  className="sticky left-0 z-10 w-44 min-w-[11rem] bg-jf-surface px-2 py-1.5 text-left font-semibold text-white sm:w-72"
                >
                  {a.atividade}
                  {a.nota && <sup className="ml-0.5 text-jf-yellow">¹</sup>}
                </th>
                {PARTES_RACI.map((p) => (
                  <td key={p.id} className={`px-1 py-1.5 text-center ${colunaClasse(p.id)}`}>
                    <CelulaRaci letra={a.celulas[p.id]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
